import TrackingKeywordsModel from './TrackingKeywordsModel'
import TrackingKeywordsController from './TrackingKeywordsController'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid2,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import PaperItem from '../../../components/Essential/PaperItem'
import { extractRootDomain, formatNumber } from '../../../utils/Utils'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useUserContext } from '../../../context/UserContext'
import TableComponent from '../../../components/TableComponent'
import * as CONSTANTS from '../../../utils/TableColumnsConstants'
import FlagSelector from '../../../components/CountryWithCodeSelector/FlagSelector'
import { countriesTracking } from '../../../utils/Countries'
import { LineChart } from '@mui/x-charts/LineChart'
import BarChartTop from '../../../components/Recharts/BarChartTop/BarChartTop'
import ComputerIcon from '@mui/icons-material/Computer'
import SmartphoneIcon from '@mui/icons-material/Smartphone'

import AppleIcon from '@mui/icons-material/Apple'
import AdbIcon from '@mui/icons-material/Adb'
import LaptopMacIcon from '@mui/icons-material/LaptopMac'
import LaptopWindowsIcon from '@mui/icons-material/LaptopWindows'

import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import TrackingKeywordsSummary from './summary/TrackingKeywordsSummary'
import BarChartIcon from '@mui/icons-material/BarChart'
import TrackingLimitError from '../errors/TrackingLimitError'
import AddIcon from '@mui/icons-material/Add'
import SerpsView from './serps/SerpsView'
import { GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid-premium'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import { useColorModeContext } from '../../../context/ColorModeContext'
import './../tracking.scss'
import { ChartsTextStyle } from '@mui/x-charts'
import TrackingSovChart from './charts/TrackingSovChart'
import TrackingKeywordCustomToolbar from './TrackingKeywordCustomToolbar'
import {
  LOCAL_STORAGE_ONBOARDING_TRACKING_KEYWORDS_FINISHED,
  ONBOARDING_STEPS_TRACKING_KEYWORDS,
  ONBOARDING_STEPS_TRACKING_PROYECTS
} from '../../../utils/Constants'
import OnboardingTour from '../../../components/Onboarding/OnboardingTour'

const TrackingKeywordsView = () => {
  const { t } = useTranslation()
  const { blur } = useUserContext()
  const { mode } = useColorModeContext()

  const model = TrackingKeywordsModel()
  const controller = TrackingKeywordsController(model)

  const osIconMap = {
    windows: LaptopWindowsIcon,
    macos: LaptopMacIcon,
    android: AdbIcon,
    ios: AppleIcon
  }

  return (
    <Grid2 container spacing={2} className='tracking'>
      {/* Onboarding Tour*/}
      {model.projects.length===1 && model.keywords.length < 2 && (
        <OnboardingTour steps={ONBOARDING_STEPS_TRACKING_KEYWORDS(t)} storageKey={LOCAL_STORAGE_ONBOARDING_TRACKING_KEYWORDS_FINISHED} />
      )}

      {/* if limit exceeded, show an error message */}
      {model.limitProjectsExceeded && (
        <TrackingLimitError title='trackingProjectLimitTitle' description='trackingProjectLimitDescription' />
      )}

      {/* if limit exceeded, show an error message */}
      {model.limitKeywordsExceeded && (
        <TrackingLimitError title='trackingKeywordLimitTitle' description='trackingKeywordLimitDescription' />
      )}

      {/* Header */}
      <Grid2 container sx={{ width: '100%' }} >

        {/* visibility */}
        <Grid2 size={{ xs: 12, md: 1.8 }} className='onbTk-1' >
          <PaperItem className='boxShadow' sx={{ p: 2, height: '100%', width: '100%' }}>
            <TrackingKeywordsSummary
              title='trackingVisibility'
              currentValue={(model.sov?.history || [])[(model.sov?.history || []).length - 1]?.sov}
              beforeValue={(model.sov?.history || [])[(model.sov?.history || []).length - 2]?.sov}
              tooltip={t('tooltip.visibility')}
              isLoading={model.isLoading.sov}
              prepend='%'
            />
          </PaperItem>
        </Grid2>

        {/* average position */}
        <Grid2 size={{ xs: 12, md: 1.8 }} >
          <PaperItem className='boxShadow' sx={{ p: 2, height: '100%', width: '100%' }}>
            <TrackingKeywordsSummary
              title='trackingPosition'
              currentValue={model.keywords.reduce((sum, keyword) => sum + keyword.keywordToday, 0 ) / model.keywords.length || 0}
              beforeValue={model.keywords.reduce((sum, keyword) => sum + keyword.trackingKeywordYesterday, 0 ) / model.keywords.length || 0}
              tooltip={t('tooltip.avgRank')}
              isLoading={model.isLoading.keywords}
              decimals={0}
            />
          </PaperItem>
        </Grid2>

        {/* num of keywords */}
        <Grid2 size={{ xs: 12, md: 1.8 }} >
          <PaperItem className='boxShadow' sx={{ p: 2, height: '100%', width: '100%' }}>
            <TrackingKeywordsSummary
              title='trackingNumKeywords'
              currentValue={model.totalKeywords || 0}
              beforeValue={model.keywords?.filter(kw => kw.keyword?.history?.length >= 2).length || 0}
              tooltip={t('tooltip.numKeywords')}
              isLoading={model.isLoading.keywords}
            />
          </PaperItem>
        </Grid2>

        {/* Chip and project selector */}
        <Grid2 size={{ xs: 12, md: 6.6 }}>
          <PaperItem className='boxShadow' sx={{ p: 2, height: '100%', width: '100%', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>

            <Box>
              <Alert
                icon={false}
                className={`tracking-message-alert ${mode}`}
              >
                {t('trackingMessage')}
              </Alert>
            </Box>

            <Tooltip title={t('usedKeywords', { count: model.projects.length, limit: model.limitKeywords })} arrow>
              <Chip
                className='onbTk-3'
                icon={<BarChartIcon />}
                label={`${model.projects.reduce((sum, p ) => sum + (p.total || 0), 0)} / ${model.limitKeywords} ${t('keywords')}`}
                color={model.limitKeywordsExceeded ? 'error' : 'default'}
                variant="filled"
                sx={{ fontWeight: 700, textTransform: 'capitalize', px: 1.5, height: '30px' }}
              />
            </Tooltip>
            <Select
              variant='outlined' value={model.projectSelected || ''} onChange={(event) => {model.setProjectSelected(event.target.value as any) }}
              displayEmpty
              className={`projectSelector ${mode} onbTk-4`}
              MenuProps={{ disableScrollLock: true }}
              sx={{ height: '30px', flexGrow: 1, minWidth: '250px' }}
            >
              <MenuItem value="" disabled style={{ display: 'none' }} >{t('project')}</MenuItem>
              {model.projects &&
                model.projects
                  .sort((a: any, b: any) => extractRootDomain(a.url).localeCompare(extractRootDomain(b.url)))
                  .map((item: any) => (
                    <MenuItem key={item.url} value={item}>
                      <span className={blur ? 'blurred' : undefined}>{item.url}</span>
                    </MenuItem>
                  ))}
            </Select>
          </PaperItem>
        </Grid2>{/* end Chip and project selector */}

      </Grid2> {/* end header */}

      {/* Graphics for the project selected */}
      <Grid2 container sx={{ width: '100%' }} >
        <PaperItem sx={{ p: 2, height: '100%', width: '100%' }}>
          <Grid2 container justifyContent='space-between' >
            <Grid2 size={{ xs: 12, sm: 12, md: 9 }} pr={2} className='onbTk-2'>
               <TrackingSovChart history={model.sov?.history} />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 4, md: 3 }} >
              <Tooltip title={t('tooltip.ranking_distribution')} followCursor arrow>
                <Typography className='title'>{t('ranking_distribution')}</Typography>
              </Tooltip>
              <Grid2 ml={-4} mt={1}>
                <BarChartTop loading={model.isLoading.ranges} data={model.ranges} height={200} />
              </Grid2>
            </Grid2>

          </Grid2>
        </PaperItem>
      </Grid2> {/* end Graphics for the project selected */}

      {/* Filters */}
      <Grid2 container sx={{ width: '100%' }}>
        <PaperItem sx={{ p: 2, height: '100%', width: '100%' }} >
          <Grid2 container justifyContent='space-between' spacing={2} mt={1}>

            {/* Flag selector and input keywords */}
            <Grid2 container size={{ xs: 12, md: (model.projectSelected && !model.projectSelected.url.includes('youtube')?4.5:10) }} spacing={1}>
              <Grid2>
                <FlagSelector countries={countriesTracking} value={model.country} onChange={(e)=> { model.setCountry(e)} } />
              </Grid2>
              <Grid2 flexGrow={1}>
                <TextField onChange={(e) => model.setInputNewKeywords(e.currentTarget.value)}
                           variant="standard" autoComplete="off" slotProps={{ input: { disableUnderline: true } }}
                           fullWidth
                           placeholder={t('trackingPlaceholderAddKeyword')}
                           className={`search ${mode} onbTk-2`}
                           multiline
                           maxRows={4}
                           sx={{ pl: 1.6, pr: 1.6 }}
                ></TextField>
              </Grid2>
            </Grid2>

            {model.projectSelected && !model.projectSelected.url.includes('youtube') &&
              <>
              {/* se */}
              <Grid2 size={{ xs: 12, md: 1.5 }}>
                <Select variant={'outlined'} value={model.searchEngine || ''}
                        onChange={(e) => model.setSearchEngine(e.target.value)}
                        displayEmpty
                        className={`projectSelector ${mode}`}
                        MenuProps={{ disableScrollLock: true }}
                        fullWidth
                        sx={{ height: '30px' }}
                >
                  <MenuItem value="google">Google</MenuItem>
                  <MenuItem value="bing">Bing</MenuItem>
                  <MenuItem value="yahoo">Yahoo</MenuItem>
                </Select>
              </Grid2>

              {/* device */}
              <Grid2 size={{ xs: 6, md: 2 }} >
                <Select variant={'outlined'} value={model.device || ''}
                        onChange={(e) => model.setDevice(e.target.value)}
                        displayEmpty
                        className={`projectSelector ${mode}`}
                        MenuProps={{ disableScrollLock: true }}
                        fullWidth
                        sx={{ height: '30px' }}
                >
                  <MenuItem value="desktop"><ComputerIcon fontSize="small" sx={{ marginRight: 1, verticalAlign: 'bottom' }} />{t('deviceDesktop')}</MenuItem>
                  <MenuItem value="mobile"><SmartphoneIcon fontSize="small" sx={{ marginRight: 1, verticalAlign: 'bottom' }} />{t('deviceMobile')}</MenuItem>
                </Select>
              </Grid2>

              {/* os */}
              <Grid2 size={{ xs: 6, md: 2 }}>
                <Select variant={'outlined'} value={model.os || ''}
                        onChange={(e) => model.setOs(e.target.value)}
                        className={`projectSelector ${mode}`}
                        MenuProps={{ disableScrollLock: true }}
                        fullWidth
                        sx={{ height: '30px' }}
                >
                  {model.osOptions[model.device].map((option) => {
                    const IconComponent = osIconMap[option]; // Dynamically select icon
                    return (
                      <MenuItem key={option} value={option}>
                        {IconComponent && <IconComponent fontSize="small" sx={{ marginRight: 1, verticalAlign: 'bottom' }} />}
                        {t(option)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Grid2>
              </>
            }

            <Grid2 size={{ xs: 12, md: 2 }}>
              <Button disabled={model.isLoading.search} className='onbTk-6' variant="contained" onClick={() => controller.handleAddKeywordsClick(model.inputNewKeywords)} sx={{ height: '30px'}} fullWidth>
                <AddIcon sx={{ marginRight: 1 }} />
                {model.isLoading.search ? (
                  <CircularProgress size={15} sx={{ bgcolor: 'transparent', color: 'white' }} />
                ) : (
                  t('keyword')
                )}
              </Button>
            </Grid2>

          </Grid2>
        </PaperItem>
      </Grid2>
      {/* end Filters */}

      {/* Table */}
      <Grid2 container sx={{ width: '100%' }} >
        <PaperItem sx={{ p: 2, height: '100%', width: '100%' }}>
          <TableComponent
            className='fullHeight'
            rowBuffer={0}
            columnBuffer={0}
            getRowHeight={() => 'auto'}
            rows={model.keywords}
            rowCount={model?.totalKeywords || 0}
            loading={model.isLoading.keywords || model.isLoading.search}
            columns={CONSTANTS.TRACKING_KEYWORDS(t, { model: model, onDelete: controller.handleOnDeleteClick, showAiOverview:model.showAiOverview && model.isAiOverview })}
            paginationModel={model.paginationModel}
            onPaginationModelChange={model.setPaginationModel}
            sortModel={model.sortModel}
            onSortModelChange={model.setSortModel}
            sortingOrder={['asc', 'desc']}
            paginationMode='server'
            sortingMode='server'
            // Panel Detail
            getDetailPanelContent={({ row }) => <SerpsView row={row} project={model.projectSelected} handleGetSerps={controller.handleGetSerps} />}
            checkboxSelection
            getRowId={(row) => row.keyword.id}
            onRowSelectionModelChange={(newSelectionModel) => {
              model.setSelectionModel(newSelectionModel);
            }}
            rowSelectionModel={model.selectionModel}
            keepNonExistentRowsSelected
            disableRowSelectionOnClick

            slots={{ toolbar: () => <TrackingKeywordCustomToolbar model={model} controller={controller} /> }}
            sx={{
              '& .MuiDataGrid-columnHeaderCheckbox': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              },
              '& .MuiDataGrid-cellCheckbox': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              },
              '& .MuiCheckbox-root': {
                transform: 'scale(0.8)',
              },
            }}

          />
        </PaperItem>
      </Grid2>{/* end Table */}

    </Grid2>
  )
}

export default TrackingKeywordsView
